import { Roles } from '../../../constants';
import { cleanNumericInputValue } from '../../../helpers';
import { ProviderStatusEnum } from '../../../pages/constants';
import { ProviderClinicalStatus } from '../../constants/general';
import {
  recognizedSupportedLanguage,
  recognizedSupportedLanguages,
} from '../EditProviderForm.utils';

import type { IProvider, ISelectOption, SelectValueType } from '../../../utils/types';
import type { RelationshipManagerPatch, RelationshipManagerPatchProvider } from './types';

export function buildRelationshipManagerPatch(
  isGuardianProvider: boolean,
  relationshipManagerProviderId: SelectValueType | null | undefined,
  provider: RelationshipManagerPatchProvider,
): RelationshipManagerPatch | Record<string, never> | null {
  if (!isGuardianProvider) {
    return {};
  }

  // Leave evv_required off this PATCH: sending the field 403s for non-admins.
  // Omit language and status unless every supplied value is already valid.
  const preferredLanguage = recognizedSupportedLanguage(provider.preferred_language);
  const supportedLanguages = recognizedSupportedLanguages(provider.supported_languages);
  const status =
    provider.status === ProviderStatusEnum.TRAINING ||
    provider.status === ProviderStatusEnum.NOT_TRAINING
      ? provider.status
      : undefined;
  const providerFields: Omit<RelationshipManagerPatch, 'relationship_manager_provider_id'> = {
    first_name: provider.first_name,
    last_name: provider.last_name,
    middle_name: provider.middle_name,
    sex: provider.sex,
    email: provider.email,
    dob: provider.dob,
    phone: cleanNumericInputValue(provider.phone ?? ''),
    npi: provider.npi,
    certification: provider.certification,
    date_certification_start: provider.date_certification_start ?? null,
    date_certification_end: provider.date_certification_end ?? null,
    state_license: provider.state_license,
    employment_type: provider.employment_type || null,
    job_title: provider.job_title || null,
    caqh_id: provider.caqh_id || '',
    payroll_id: provider.payroll_id || '',
    street_address_line_one: provider.street_address_line_one,
    street_address_line_two: provider.street_address_line_two,
    street_address_city: provider.street_address_city,
    street_address_state: provider.street_address_state,
    street_address_zip: provider.street_address_zip,
  };
  if (preferredLanguage !== undefined) {
    providerFields.preferred_language = preferredLanguage;
  }
  if (supportedLanguages !== undefined) {
    providerFields.supported_languages = supportedLanguages;
  }
  if (status !== undefined) {
    providerFields.status = status;
  }

  if (relationshipManagerProviderId === '' || relationshipManagerProviderId == null) {
    return { ...providerFields, relationship_manager_provider_id: null };
  }

  const parsedRelationshipManagerId = Number(relationshipManagerProviderId);
  if (!Number.isFinite(parsedRelationshipManagerId)) {
    return null;
  }

  return {
    ...providerFields,
    relationship_manager_provider_id: parsedRelationshipManagerId,
  };
}

export function getRelationshipManagerOptions(candidates: IProvider[]) {
  return [...candidates]
    .sort((left, right) =>
      `${left.first_name} ${left.last_name}`.localeCompare(
        `${right.first_name} ${right.last_name}`,
      ),
    )
    .map((candidate) => ({
      value: candidate.id,
      label: `${candidate.first_name} ${candidate.last_name} (${candidate.email})`,
    }));
}

export function getRelationshipManagerDisplayValue(
  selected: SelectValueType,
  candidates: IProvider[],
) {
  if (selected === '' || selected == null) {
    return 'Select relationship manager';
  }

  const selectedId = Number(selected);
  const selectedCandidate = Number.isFinite(selectedId)
    ? candidates.find((candidate) => candidate.id === selectedId)
    : undefined;
  return selectedCandidate
    ? `${selectedCandidate.first_name} ${selectedCandidate.last_name}`
    : 'Select relationship manager';
}

export function getOptions(clinicalStatus: ProviderClinicalStatus | undefined) {
  // An invited parent may only be revoked to OFFBOARDED or PRE_CLINICAL. Treat
  // GUARDIAN_INVITED like OFFBOARDED/PRE_CLINICAL here: the backend rejects a move
  // to ON_HOLD or LIMITED_ACCESS_GUARDIAN from it.
  const isRevokeOnlyStatus =
    clinicalStatus === ProviderClinicalStatus.OFFBOARDED ||
    clinicalStatus === ProviderClinicalStatus.PRE_CLINICAL ||
    clinicalStatus === ProviderClinicalStatus.GUARDIAN_INVITED;

  return Object.values(ProviderClinicalStatus)
    .filter((val) => {
      // The promote flow owns CLINICAL and the invite-to-platform flow owns
      // GUARDIAN_INVITED; neither is a manual status option.
      if (
        val === clinicalStatus ||
        val === ProviderClinicalStatus.CLINICAL ||
        val === ProviderClinicalStatus.GUARDIAN_INVITED
      ) {
        return false;
      }

      if (
        isRevokeOnlyStatus &&
        (val === ProviderClinicalStatus.ON_HOLD ||
          val === ProviderClinicalStatus.LIMITED_ACCESS_GUARDIAN)
      ) {
        return false;
      }

      if (
        clinicalStatus === ProviderClinicalStatus.LIMITED_ACCESS_GUARDIAN &&
        val === ProviderClinicalStatus.ON_HOLD
      ) {
        return false;
      }

      return true;
    })
    .map((val) => ({ value: val, label: val }));
}

export function isPromoteRoleOptionDisabled(
  option: ISelectOption,
  selectedRoles: ISelectOption[],
) {
  const selectedValues = selectedRoles.map((role) => role.value);
  if (selectedValues.includes(Roles.Prbt) && option.value !== Roles.Prbt) {
    return true;
  }
  if (option.value === Roles.Prbt && selectedValues.length > 0) {
    return true;
  }
  return false;
}

export function toRoleStrings(selectedRoles: ISelectOption[]) {
  return selectedRoles.map((role) => String(role.value));
}

export function formatProviderSsnInput(value: string) {
  const digits = value.replace(/[^0-9]/g, '').slice(0, 9);

  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}
